import { ILeavingParams } from "../models/Behavior/ILeavingParams";
import { PageContext } from "../service/PageContext";
import { DomUtil } from "../util/DomUtil";
import { moduletracking } from "../Wyanoke/ModuleTracking";
import { UiUtil } from "../util/UiUtil";
import { Cookie } from "../util/CookieUtil";

export class Leaving {

  private _isBound = false;
  private currentTargetElement: HTMLAnchorElement | undefined = undefined;
  private readonly _ignoreTargets = ["_blank", "_new", "media"];
  private readonly _ignoreExtensions = [".png", ".gif", ".jpg", ".ashx"];
  private readonly _ignoreClasses = ["pds-vote-button",
    "pds-vote-button", "pds-view-results", "btn", "print"];
  private readonly _leaveElement: HTMLElement | null;
  private readonly _dontShowElement: HTMLInputElement | null;
  private readonly _trackingCategory: string;
  private readonly _trackingUrl: string;
  private readonly _externalLinksFilter: ((element: HTMLAnchorElement, index: number, array: HTMLAnchorElement[]) => boolean) | null;

  constructor(
    private readonly _element: HTMLElement,
    context: PageContext | null,
    leavingParams: ILeavingParams
  ) {
    this._leaveElement = this._element.querySelector("[data-modal-action='leave']");
    this._dontShowElement = this._element.querySelector("[data-modal-action='dontshow']");
    this._trackingUrl = leavingParams?.trackingUrl ?? "";
    this._trackingCategory = leavingParams?.trackingCategory ?? "";
    this._externalLinksFilter = leavingParams.externalLinksFilter;
  }

  // This was our original selector here for reference:
  // $("a:not([href*=javascript],[href^='#'],[data-serieslink]," +
  //  ".pds-vote-button, .pds-view-results, .btn,.print," +
  //  "[target=_blank],[target=_new],[target=media]," +
  //  "[href*='" + this._activityUrl + "']," +
  //  "[href$='.png'],[href$='.gif'],[href$='.jpg']," +
  //   "[href$='.ashx'])")

  public attachToExternalLinks(): void {
    let allAElements = DomUtil.queryAll<HTMLAnchorElement>(document.body, "a");
    if (this._externalLinksFilter) {
      allAElements = allAElements.filter(this._externalLinksFilter);
    }

    const filteredAElements =
      allAElements
        .filter(anchor => !this._element.contains(anchor))
        .filter(anchor => anchor.getAttribute("href") && !anchor.getAttribute("href")?.startsWith("#"))
        .filter(anchor => anchor.getAttribute("href") && anchor.getAttribute("href")?.indexOf("javascript") === -1)
        .filter(anchor => anchor.getAttribute("data-serieslink") === null)
        .filter(anchor => this._ignoreTargets.indexOf(anchor.target) === -1)
        .filter(anchor => !this._ignoreClasses.some(aClass =>
          anchor.className.indexOf(aClass) !== -1))
        .filter(anchor => !this._ignoreExtensions.some(extension =>
          anchor.href.endsWith(extension)));

    filteredAElements
      .forEach(aElement => {
        if (window.$j) {
          window.$j(aElement).off("click");
        }
        $(aElement).off("click");
        aElement.addEventListener("click", ev =>
          this.handleExternalLinkClick(ev), true);
      });
  }

  public async bind(): Promise<boolean> {

    if (window.modtrkloaded !== undefined) {
      this.attachToExternalLinks();
    } else {
      window.addEventListener(moduletracking.ModuleTrackingLoadedEvent,
        () => this.attachToExternalLinks());
    }

    if (this._dontShowElement) {
      this._dontShowElement.addEventListener("click", ev => {
        this.handleDontShow(ev);
      });
    }

    if (this._leaveElement) {
      this._leaveElement.addEventListener("click", ev => {
        UiUtil.toggleButtonState(this._leaveElement as HTMLButtonElement);
        this.handleLeavingConfirmed(ev);
      });
    } else {
      console.warn("Unable to find leave element and navigate away from page.");

      return false;
    }

    this._isBound = true;

    return true;
  }

  private handleDontShow(ev: Event):void {
    const cookiename = this._element.getAttribute("data-modal");
    const cookielifetime = 315360000;
    const mincookielength = 1;
    if(cookiename === undefined || cookiename === null || cookiename.length < mincookielength) {

      return;
    }

    if(this._dontShowElement?.checked) {
      Cookie.setCookie(cookiename, "1", cookielifetime);
    } else {
      Cookie.deleteCookie(cookiename);
    }
  }

  private handleExternalLinkClick(ev: Event): void {
    if (!this._isBound) {

      return;
    }

    ev.preventDefault();
    ev.stopPropagation();
    const linkClicked = ev.currentTarget as HTMLAnchorElement;
    this.currentTargetElement = linkClicked;
    $(this._element).modal("show");
  }

  private handleLeavingConfirmed(ev: Event): void {
    ev.preventDefault();
    // if we do need to call something to save we need to do that here
    if (this.currentTargetElement === undefined) {
      console.error("No target element for leaving. Unable to find redirect href.");

      return;
    }

    const url = this.currentTargetElement.href;
    // Justification: a type defintion was added for the "trackClick" function
    // just in case it is not on the page (currently in combodeferred js)
    // tslint:disable-next-line: strict-type-predicates
    if (typeof (trackClick) === "function" && this._trackingCategory && this._trackingUrl) {
      trackClick(url, this._trackingCategory, url, this._trackingUrl, 0);
    } else {
      console.warn("Link click not tracked: trackClick function not defined");
      window.location.href = url;
    }
  }

}
